import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import { Checkbox } from "@/components/Checkbox/Checkbox";
import { AutoCompleteItem, Dropdown } from "@/components/Dropdown/Dropdown";
import { useAppSelector } from "@/redux/hooks";
import { useSnackBar } from "@/utils/useSnackbar";
import {
	useGetCompanyDealCreationTriggerQuery,
	useGetCompanyLeadSyncTriggerQuery,
	useGetCompanySyncTriggerQuery,
	useGetConversationSyncQuery,
	useUpdateCompanyDealCreationTriggerMutation,
	useUpdateCompanyLeadSyncTriggerMutation,
	useUpdateCompanySyncTriggerMutation,
	useUpdateConversationSyncMutation,
} from "@/pages/Private/redux/hubspot/hubspot.api";
import { hubspotSelector } from "@/pages/Private/redux/hubspot/hubspot.slice";

import {
	HubspotDealCreationTrigger,
	HubspotLeadSyncTrigger,
	HubspotSyncTrigger,
} from "../schema/hubspot";
import { HubspotResyncSection } from "./HubspotResyncSection";

interface HubspotSettingsTabProps {
	isConnected: boolean;
}

export const HubspotSettingsTab: FunctionComponent<HubspotSettingsTabProps> = ({ isConnected }) => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`company.${key}`);
	const { handleOpenSnackbar, renderMessageSnackbar } = useSnackBar();
	const {
		companySyncTrigger,
		companyDealCreationTrigger,
		companyLeadSyncTrigger,
		conversationSync,
	} = useAppSelector(hubspotSelector);

	useGetCompanySyncTriggerQuery(undefined, { skip: !isConnected });
	useGetCompanyDealCreationTriggerQuery(undefined, { skip: !isConnected });
	useGetCompanyLeadSyncTriggerQuery(undefined, { skip: !isConnected });
	useGetConversationSyncQuery(undefined, { skip: !isConnected });

	const [updateCompanySyncTrigger] = useUpdateCompanySyncTriggerMutation();
	const [updateCompanyDealCreationTrigger] = useUpdateCompanyDealCreationTriggerMutation();
	const [updateCompanyLeadSyncTrigger] = useUpdateCompanyLeadSyncTriggerMutation();
	const [updateConversationSync] = useUpdateConversationSyncMutation();

	const syncTriggerOptions: AutoCompleteItem[] = [
		{ id: HubspotSyncTrigger.DISABLED, title: ts("hubspot.syncTrigger.DISABLED") },
		{
			id: HubspotSyncTrigger.ON_CAMPAIGN_START,
			title: ts("hubspot.syncTrigger.ON_CAMPAIGN_START"),
		},
		{ id: HubspotSyncTrigger.ON_REPLY, title: ts("hubspot.syncTrigger.ON_REPLY") },
	];
	const responseTriggerOptions = [
		{ id: HubspotLeadSyncTrigger.DISABLED, title: ts("hubspot.leadSyncTrigger.DISABLED") },
		{ id: HubspotLeadSyncTrigger.ON_RESPONSE, title: ts("hubspot.leadSyncTrigger.ON_RESPONSE") },
		{
			id: HubspotLeadSyncTrigger.ON_POSITIVE_RESPONSE,
			title: ts("hubspot.leadSyncTrigger.ON_POSITIVE_RESPONSE"),
		},
	];
	const dealTriggerOptions = responseTriggerOptions.map((option) => ({
		...option,
		title: ts(`hubspot.dealCreationTrigger.${option.id}`),
	}));

	const toggleConversationSync = async () => {
		try {
			await updateConversationSync({ enabled: !(conversationSync?.enabled ?? false) }).unwrap();
			handleOpenSnackbar(ts("hubspot.conversationSync.success"));
		} catch {
			handleOpenSnackbar(ts("hubspot.conversationSync.error"));
		}
	};

	return (
		<div className="flex w-full flex-col">
			{renderMessageSnackbar()}
			<TriggerDropdown
				description={ts("hubspot.syncTrigger.description")}
				label={ts("hubspot.syncTrigger.title")}
				options={syncTriggerOptions}
				value={companySyncTrigger?.trigger}
				onSelect={(value) => updateCompanySyncTrigger({ trigger: value as HubspotSyncTrigger })}
			/>
			<TriggerDropdown
				description={ts("hubspot.leadSyncTrigger.description")}
				label={ts("hubspot.leadSyncTrigger.title")}
				options={responseTriggerOptions}
				value={companyLeadSyncTrigger?.trigger}
				onSelect={(value) =>
					updateCompanyLeadSyncTrigger({ trigger: value as HubspotLeadSyncTrigger })
				}
			/>
			<TriggerDropdown
				description={ts("hubspot.dealCreationTrigger.description")}
				label={ts("hubspot.dealCreationTrigger.title")}
				options={dealTriggerOptions}
				value={companyDealCreationTrigger?.trigger}
				onSelect={(value) =>
					updateCompanyDealCreationTrigger({ trigger: value as HubspotDealCreationTrigger })
				}
			/>
			<div className="mt-6 border-t border-border-light pt-6">
				<div className="mb-2 text-sm font-medium text-text">
					{ts("hubspot.conversationSync.title")}
				</div>
				<Checkbox
					isChecked={conversationSync?.enabled ?? false}
					name="conversationSync"
					onChange={toggleConversationSync}
				>
					{ts("hubspot.conversationSync.label")}
				</Checkbox>
				<div className="mt-2 text-sm text-text-meta">
					{ts("hubspot.conversationSync.description")}
				</div>
			</div>
			<HubspotResyncSection />
		</div>
	);
};

interface TriggerDropdownProps {
	description: string;
	label: string;
	options: AutoCompleteItem[];
	value?: string;
	onSelect: (value: string) => void;
}

const TriggerDropdown: FunctionComponent<TriggerDropdownProps> = ({
	description,
	label,
	options,
	value,
	onSelect,
}) => (
	<div className="mb-6 max-w-xl">
		<div className="mb-3 text-sm text-text-meta">{description}</div>
		<Dropdown
			data={options}
			defaultValue={value ? options.find((option) => option.id === value) : undefined}
			floating={true}
			handleSelect={(item: AutoCompleteItem) => onSelect(String(item.id))}
			label={label}
		/>
	</div>
);
